import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Sparkles,
  Plus,
  Copy,
  Trash2,
  Edit3,
  Save,
  X as XIcon,
  Loader2,
  Shield,
  AlertCircle,
} from 'lucide-react';
import {
  AdminPipeline,
  cloneAdminPipeline,
  createAdminPipeline,
  deleteAdminPipeline,
  listAdminPipelines,
  updateAdminPipeline,
} from '../services/smartStartApi';
import { useAuth } from '../contexts/AuthContext';
import { useSmartStartModal } from '../components/SmartStart/Modal';

const EMPTY_STAGE = {
  key: '',
  name: '',
  module: 'intake',
  short_description: '',
  requires_review: false,
};

interface SmartStartPipelineAdminProps {
  embedded?: boolean;
}

const SmartStartPipelineAdmin: React.FC<SmartStartPipelineAdminProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState<AdminPipeline[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editDraft, setEditDraft] = useState<Partial<AdminPipeline> | null>(null);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const { modal: smartModal, confirm: modalConfirm, prompt: modalPrompt } = useSmartStartModal();

  const loadPipelines = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listAdminPipelines();
      setPipelines(list);
      setSelectedId((prev) => (prev == null && list.length > 0 ? list[0].id : prev));
    } catch (err: any) {
      toast.error(`Could not load pipelines: ${err?.response?.data?.error || err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (!isAdmin) return;
    if (didInitialLoad.current) return;
    didInitialLoad.current = true;
    loadPipelines();
  }, [isAdmin, loadPipelines]);

  const selected = useMemo(
    () => pipelines.find((p) => p.id === selectedId) || null,
    [pipelines, selectedId]
  );

  const startEdit = () => {
    if (!selected) return;
    if (selected.is_system) {
      toast.error('System pipelines are read-only. Clone first.');
      return;
    }
    setEditDraft({
      name: selected.name,
      description: selected.description || '',
      use_cases: [...(selected.use_cases || [])],
      routing_keywords: [...(selected.routing_keywords || [])],
      stages: JSON.parse(JSON.stringify(selected.stages || [])),
      final_artifact_types: [...(selected.final_artifact_types || [])],
      enabled: selected.enabled,
    });
  };

  const cancelEdit = () => setEditDraft(null);

  const saveEdit = async () => {
    if (!selected || !editDraft) return;

    const name = (editDraft.name as string | undefined)?.trim();
    if (!name) {
      toast.error('Pipeline name is required.');
      return;
    }
    const stages = Array.isArray(editDraft.stages) ? (editDraft.stages as any[]) : [];
    if (stages.length === 0) {
      toast.error('Pipeline must have at least one stage.');
      return;
    }
    const validModules = new Set(['intake', 'dcr', 'sda', 'ai_council', 'project_management', 'export']);
    const keys = new Set<string>();
    for (let i = 0; i < stages.length; i += 1) {
      const s = stages[i];
      if (!s.key || typeof s.key !== 'string') {
        toast.error(`Stage ${i + 1}: key is required.`);
        return;
      }
      if (keys.has(s.key)) {
        toast.error(`Stage ${i + 1}: duplicate key "${s.key}".`);
        return;
      }
      keys.add(s.key);
      if (!s.name || typeof s.name !== 'string') {
        toast.error(`Stage ${i + 1}: name is required.`);
        return;
      }
      if (!validModules.has(s.module)) {
        toast.error(`Stage ${i + 1}: invalid module "${s.module}".`);
        return;
      }
    }

    try {
      setIsSaving(true);
      const updated = await updateAdminPipeline(selected.id, editDraft);
      setPipelines((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setEditDraft(null);
      toast.success('Pipeline updated.');
    } catch (err: any) {
      toast.error(`Save failed: ${err?.response?.data?.error || err.message || err}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async (p: AdminPipeline) => {
    if (p.is_system) {
      toast.error('System pipelines are read-only. Clone first.');
      return;
    }
    try {
      const updated = await updateAdminPipeline(p.id, { enabled: !p.enabled });
      setPipelines((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (err: any) {
      toast.error(`Update failed: ${err?.response?.data?.error || err.message || err}`);
    }
  };

  const handleClone = async () => {
    if (!selected) return;
    const answers = await modalPrompt({
      title: `Clone "${selected.name}"`,
      description:
        'Clones create a tenant-scoped copy you can freely edit. System pipelines remain read-only.',
      confirmLabel: 'Clone pipeline',
      fields: [
        {
          name: 'suffix',
          label: 'Suffix',
          placeholder: 'e.g. smbc',
          defaultValue: 'custom',
          required: true,
          pattern: /^[a-zA-Z0-9_-]+$/,
          patternMessage: 'Only letters, numbers, underscores, and hyphens are allowed.',
          help: 'A short tag appended to the cloned pipeline key (lowercased automatically on the server).',
          autoFocus: true,
        },
      ],
    });
    if (!answers) return;
    try {
      const cloned = await cloneAdminPipeline(selected.id, { suffix: answers.suffix });
      await loadPipelines();
      setSelectedId(cloned.id);
      toast.success('Pipeline cloned.');
    } catch (err: any) {
      toast.error(`Clone failed: ${err?.response?.data?.error || err.message || err}`);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (selected.is_system) {
      toast.error('System pipelines cannot be deleted.');
      return;
    }
    const ok = await modalConfirm({
      title: `Delete "${selected.name}"?`,
      description:
        'This permanently removes the pipeline from this tenant. Sessions already started from it will keep working because each session snapshots its own copy.',
      variant: 'danger',
      confirmLabel: 'Delete pipeline',
      cancelLabel: 'Keep',
    });
    if (!ok) return;
    try {
      await deleteAdminPipeline(selected.id);
      setSelectedId(null);
      await loadPipelines();
      toast.success('Pipeline deleted.');
    } catch (err: any) {
      toast.error(`Delete failed: ${err?.response?.data?.error || err.message || err}`);
    }
  };

  const handleCreateBlank = async () => {
    const answers = await modalPrompt({
      title: 'Create new pipeline',
      description: 'Give your pipeline a unique key and a display name. You can edit everything after it is created.',
      confirmLabel: 'Create pipeline',
      fields: [
        {
          name: 'name',
          label: 'Display name',
          placeholder: 'e.g. SMBC Budget Justification',
          defaultValue: 'New Custom Pipeline',
          required: true,
          help: 'What users will see in Smart Start.',
          autoFocus: true,
        },
        {
          name: 'pipeline_key',
          label: 'Pipeline key',
          placeholder: 'custom_pipeline_1234',
          defaultValue: `custom_pipeline_${Date.now()}`,
          required: true,
          pattern: /^[a-zA-Z0-9_-]+$/,
          patternMessage: 'Only letters, numbers, underscores, and hyphens.',
          help: 'Stable identifier used by the classifier. Not shown to users.',
        },
      ],
    });
    if (!answers) return;
    try {
      const created = await createAdminPipeline({
        pipeline_key: answers.pipeline_key,
        name: answers.name,
        description: '',
        use_cases: [],
        routing_keywords: [],
        stages: [{ ...EMPTY_STAGE, key: 'intake_start', name: 'Intake' }],
        final_artifact_types: [],
        enabled: true,
      });
      await loadPipelines();
      setSelectedId(created.id);
      toast.success('Pipeline created.');
    } catch (err: any) {
      toast.error(`Create failed: ${err?.response?.data?.error || err.message || err}`);
    }
  };

  if (!isAdmin) {
    const accessDenied = (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-start gap-3">
        <Shield className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-lg font-bold text-gray-900">Admin access required</div>
          <p className="text-sm text-gray-600">
            You need an ADMIN or SUPER_ADMIN role to manage Smart Start pipelines.
          </p>
        </div>
      </div>
    );
    if (embedded) return accessDenied;
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-3xl mx-auto">{accessDenied}</div>
      </div>
    );
  }

  const headerBlock = (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-lavender/10 rounded-xl">
          <Sparkles className="w-5 h-5 text-lavender" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {embedded ? 'Smart Start Admin' : 'Smart Start · Pipeline Admin'}
          </h1>
          <p className="text-sm text-gray-600">
            Manage the pipelines that Smart Start routes users into. System pipelines are read-only — clone to customize.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        {!embedded && (
          <button
            onClick={() => navigate('/app/smart-start')}
            className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 bg-white border border-gray-200 rounded-lg"
          >
            Back to Smart Start
          </button>
        )}
        <button
          onClick={handleCreateBlank}
          className="flex items-center gap-2 text-sm font-semibold px-3 py-1.5 bg-lavender text-white rounded-lg hover:bg-lavender-deep"
        >
          <Plus className="w-4 h-4" />
          New pipeline
        </button>
      </div>
    </div>
  );

  const body = (
    <>
      {headerBlock}

        {loading ? (
          <div className="flex items-center gap-2 p-6 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading pipelines…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-3">
              <ul className="divide-y divide-gray-100">
                {pipelines.map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={() => {
                        setSelectedId(p.id);
                        setEditDraft(null);
                      }}
                      className={`w-full text-left p-3 rounded-lg transition ${
                        selectedId === p.id ? 'bg-lavender/5' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                        <div className="flex items-center gap-1">
                          {p.is_system && (
                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                              System
                            </span>
                          )}
                          {!p.enabled && (
                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                              Off
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{p.pipeline_key}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(p.stages || []).length} stage{(p.stages || []).length === 1 ? '' : 's'}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              {!selected ? (
                <div className="text-sm text-gray-500">Select a pipeline to view details.</div>
              ) : editDraft ? (
                <PipelineEditor
                  draft={editDraft}
                  setDraft={setEditDraft}
                  onCancel={cancelEdit}
                  onSave={saveEdit}
                  isSaving={isSaving}
                />
              ) : (
                <>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                        {selected.pipeline_key}
                      </div>
                      <h2 className="text-lg font-bold text-gray-900">{selected.name}</h2>
                      {selected.description && (
                        <p className="text-sm text-gray-600 mt-1 max-w-2xl">{selected.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!selected.is_system && (
                        <button
                          onClick={() => handleToggleEnabled(selected)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
                            selected.enabled
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {selected.enabled ? 'Enabled' : 'Disabled'}
                        </button>
                      )}
                      <button
                        onClick={handleClone}
                        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <Copy className="w-3.5 h-3.5" /> Clone
                      </button>
                      {!selected.is_system && (
                        <>
                          <button
                            onClick={startEdit}
                            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={handleDelete}
                            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {selected.is_system && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        This is a system pipeline — read-only. Clone it to create an editable tenant-scoped copy.
                      </div>
                    </div>
                  )}

                  <Section title="Use cases">
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                      {(selected.use_cases || []).map((u, i) => (
                        <li key={i}>{u}</li>
                      ))}
                    </ul>
                  </Section>

                  <Section title="Routing keywords">
                    <div className="flex flex-wrap gap-1.5">
                      {(selected.routing_keywords || []).map((k, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-700">
                          {k}
                        </span>
                      ))}
                    </div>
                  </Section>

                  <Section title="Stages">
                    <ol className="space-y-2">
                      {(selected.stages || []).map((s: any, i: number) => (
                        <li key={i} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-semibold">
                              {i + 1}
                            </div>
                            <div className="text-sm font-semibold text-gray-900">{s.name}</div>
                            <span className="text-xs px-2 py-0.5 bg-white border border-gray-200 rounded-full text-gray-600">
                              {s.module}
                            </span>
                            {s.requires_review && (
                              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                Review
                              </span>
                            )}
                          </div>
                          {s.short_description && (
                            <p className="text-xs text-gray-600 mt-1 ml-9">{s.short_description}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  </Section>
                </>
              )}
            </div>
          </div>
        )}

      {smartModal}
    </>
  );

  if (embedded) return body;
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">{body}</div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-5">
    <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1.5">{title}</div>
    {children}
  </div>
);

interface EditorProps {
  draft: Partial<AdminPipeline>;
  setDraft: (d: Partial<AdminPipeline>) => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
}

const PipelineEditor: React.FC<EditorProps> = ({ draft, setDraft, onCancel, onSave, isSaving }) => {
  const updateField = <K extends keyof AdminPipeline>(key: K, value: AdminPipeline[K]) => {
    setDraft({ ...draft, [key]: value });
  };

  const stages: any[] = Array.isArray(draft.stages) ? draft.stages : [];

  const updateStage = (idx: number, patch: any) => {
    const next = stages.slice();
    next[idx] = { ...next[idx], ...patch };
    updateField('stages', next as any);
  };

  const addStage = () => {
    updateField('stages', [...stages, { ...EMPTY_STAGE, key: `stage_${stages.length + 1}` }] as any);
  };

  const removeStage = (idx: number) => {
    updateField('stages', stages.filter((_, i) => i !== idx) as any);
  };

  const moveStage = (idx: number, delta: number) => {
    const next = stages.slice();
    const swap = idx + delta;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    updateField('stages', next as any);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">Editing pipeline</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <XIcon className="w-4 h-4" /> Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 bg-lavender text-white rounded-lg hover:bg-lavender-deep disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Name</label>
          <input
            type="text"
            value={(draft.name as string) || ''}
            onChange={(e) => updateField('name', e.target.value as any)}
            className="w-full mt-1 p-2 text-sm rounded-lg border border-gray-300 focus:border-lavender focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Enabled</label>
          <select
            value={draft.enabled ? 'true' : 'false'}
            onChange={(e) => updateField('enabled', (e.target.value === 'true') as any)}
            className="w-full mt-1 p-2 text-sm rounded-lg border border-gray-300 focus:border-lavender focus:outline-none"
          >
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Description</label>
        <textarea
          value={(draft.description as string) || ''}
          onChange={(e) => updateField('description', e.target.value as any)}
          rows={3}
          className="w-full mt-1 p-2 text-sm rounded-lg border border-gray-300 focus:border-lavender focus:outline-none resize-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
            Use cases (one per line)
          </label>
          <textarea
            value={(draft.use_cases || []).join('\n')}
            onChange={(e) =>
              updateField('use_cases', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) as any)
            }
            rows={5}
            className="w-full mt-1 p-2 text-sm rounded-lg border border-gray-300 focus:border-lavender focus:outline-none resize-none"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
            Routing keywords (comma separated)
          </label>
          <textarea
            value={(draft.routing_keywords || []).join(', ')}
            onChange={(e) =>
              updateField(
                'routing_keywords',
                e.target.value.split(',').map((s) => s.trim()).filter(Boolean) as any
              )
            }
            rows={5}
            className="w-full mt-1 p-2 text-sm rounded-lg border border-gray-300 focus:border-lavender focus:outline-none resize-none"
          />
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Stages</label>
          <button
            onClick={addStage}
            className="text-xs font-semibold px-2 py-1 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            + Add stage
          </button>
        </div>
        <ol className="space-y-2">
          {stages.map((s: any, idx: number) => (
            <li key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_1fr_120px_100px] gap-2 items-start">
                <div className="text-xs text-gray-500 pt-2">#{idx + 1}</div>
                <input
                  type="text"
                  value={s.key || ''}
                  onChange={(e) => updateStage(idx, { key: e.target.value })}
                  placeholder="stage_key"
                  className="p-2 text-xs rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={s.name || ''}
                  onChange={(e) => updateStage(idx, { name: e.target.value })}
                  placeholder="Stage name"
                  className="p-2 text-xs rounded border border-gray-300"
                />
                <select
                  value={s.module || 'intake'}
                  onChange={(e) => updateStage(idx, { module: e.target.value })}
                  className="p-2 text-xs rounded border border-gray-300 bg-white"
                >
                  <option value="intake">intake</option>
                  <option value="dcr">dcr</option>
                  <option value="sda">sda</option>
                  <option value="ai_council">ai_council</option>
                  <option value="project_management">project_management</option>
                  <option value="export">export</option>
                </select>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveStage(idx, -1)}
                    className="text-xs px-2 py-1 bg-white border border-gray-300 rounded"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveStage(idx, 1)}
                    className="text-xs px-2 py-1 bg-white border border-gray-300 rounded"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => removeStage(idx)}
                    className="text-xs px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-[1fr_120px] gap-2">
                <input
                  type="text"
                  value={s.short_description || ''}
                  onChange={(e) => updateStage(idx, { short_description: e.target.value })}
                  placeholder="Short description"
                  className="p-2 text-xs rounded border border-gray-300"
                />
                <label className="flex items-center gap-1 text-xs text-gray-600 px-2">
                  <input
                    type="checkbox"
                    checked={!!s.requires_review}
                    onChange={(e) => updateStage(idx, { requires_review: e.target.checked })}
                  />
                  Requires review
                </label>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default SmartStartPipelineAdmin;
